import { z } from 'zod';
import { LedgerEntrySchema, LedgerAccountSystemRoleSchema, LedgerKindSchema, MAX_AS_OF_SERIES_COLUMNS, MIN_AS_OF_SERIES_COLUMNS, } from '../ledger.js';
import { BankAccountSchema } from './account.js';
import { CalendarDateSchema, LowercaseUuidSchema, MAX_SEND_BCC_RECIPIENTS } from './common.js';
import { nullableText } from '../sanitised-text.js';
import { DocumentPaymentStatusSchema, PaymentDirectionSchema, PaymentFundingSourceSchema } from './document-payment.js';
// =============================================================================
// Client Statement
// =============================================================================
export const ClientStatementEntrySchema = LedgerEntrySchema.extend({
    accountingDate: z.string(), // YYYY-MM-DD
    description: z.string(),
    clientProjectReference: z.string().nullable(),
    // The raw bank-statement-line text this entry was reconciled from (e.g. "EFT
    // PAYMENT REF12345 JOHN SMITH"). Always null on the AR statement (invoice
    // entries have no bank-line source) and on a trust statement entry that was
    // never reconciled from a real bank line (a document-free mint).
    bankLineDescription: z.string().nullable(),
    // The firm's own wording for a document-free client account payment, shown
    // as the description subtitle. The row's description is a fixed label
    // naming the kind of entry; this says which case it is (a balance carried
    // over from a previous system, a correction) in the firm's words. Null on
    // every entry that is not one of those payments.
    accountPaymentReference: z.string().nullable(),
    runningBalanceMinorUnits: z.number(),
});
/**
 * A single-ledger client statement using the ledger's debit-positive
 * convention for every bare amount and balance field. For BUSINESS, positive
 * means fees owing. For TRUST_CREDITORS, negative means funds held for the
 * client and positive means a trust shortfall.
 */
export const ClientStatementResponseSchema = z.object({
    entries: z.array(ClientStatementEntrySchema),
    period: z.object({ from: z.string().nullable(), to: z.string() }),
    openingBalanceMinorUnits: z.number(),
    closingBalanceMinorUnits: z.number(),
    currency: z.string(),
});
// =============================================================================
// Client Account Payments
// =============================================================================
export const ClientAccountPaymentFundingSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('bankAccount'),
        bankAccountId: z.string().uuid('Bank account ID must be a valid UUID'),
    }),
    // One ledger-funded member covers every balance movement with no bank
    // behind it: a balance carried over from the firm's previous system, and a
    // correction to a client's account. Both post the same contra to retained
    // earnings and are indistinguishable once stored, so a discriminator
    // separating them would claim a distinction a stored row cannot carry.
    //
    // What separates them for the person reading the statement is the wording,
    // so the firm writes it. `description` becomes the settlement journal's
    // description verbatim, which prints on the statement, the statement PDF
    // and the XLSX export - it is client-facing text, not an internal
    // narration, which is why it is required and capped for a table cell
    // rather than for a notes field.
    z.object({
        source: z.literal('balanceAdjustment'),
        description: z.string()
            .trim()
            .min(1, 'Say what this entry is for')
            .max(80, 'Description must be 80 characters or less'),
    }),
]);
export const RecordClientAccountPaymentBodySchema = z.object({
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
    direction: PaymentDirectionSchema,
    funding: ClientAccountPaymentFundingSchema,
    amountMinorUnits: z.number().int().min(1, 'Payment amount must be greater than zero'),
    paidAt: CalendarDateSchema,
    reference: nullableText(255, 'Reference must be 255 characters or less'),
    notes: nullableText(2000, 'Notes must be 2000 characters or less'),
});
export const ClientAccountPaymentSchema = z.object({
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    clientId: z.string().uuid(),
    direction: PaymentDirectionSchema,
    fundingSource: PaymentFundingSourceSchema.extract(['BANK_ACCOUNT', 'LEDGER_ACCOUNT']),
    amountMinorUnits: z.number().int(),
    currency: z.string(),
    paidAt: z.string(),
    status: DocumentPaymentStatusSchema,
    ledgerEntrySetId: z.string().uuid().nullable(),
    bankStatementLineId: z.string().uuid().nullable(),
    reference: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string(),
});
export const ListClientAccountPaymentsResponseSchema = z.object({
    payments: z.array(ClientAccountPaymentSchema),
});
export const ClientAccountPaymentFundingOptionsResponseSchema = z.object({
    bankAccounts: z.array(BankAccountSchema),
});
// =============================================================================
// Combined Client Statement
// =============================================================================
export const CombinedClientStatementEntrySchema = z.object({
    id: z.string().uuid(),
    entrySetId: z.string().uuid(),
    ledgerKind: LedgerKindSchema,
    accountingDate: z.string(),
    description: z.string(),
    debitMinorUnits: z.number().int().nonnegative(),
    creditMinorUnits: z.number().int().nonnegative(),
    currency: z.string(),
    invoiceId: z.string().uuid().nullable(),
    creditNoteId: z.string().uuid().nullable(),
    clientProjectReference: z.string().nullable(),
    bankLineDescription: z.string().nullable(),
});
// =============================================================================
// Client Statement Feeds
// GET /accounts/:accountId/clients/:clientId/statement-feeds
// =============================================================================
/**
 * One interleaved feed entry: `CombinedClientStatementEntrySchema` extended
 * with a server-computed signed amount and two server-computed running
 * balances, so the frontend never derives either itself.
 *   - `amountMinorUnits` - the raw ledger-entry amount, debit-positive
 *     (matching the underlying ledger's own sign convention): equal to
 *     `debitMinorUnits - creditMinorUnits`, provided so no caller needs to
 *     re-derive that subtraction from the two non-negative columns. This is
 *     NOT the trust "funds held" presentation sign - that negation happens
 *     only in `position` below, never here.
 *   - `runningDebitPositiveMinorUnits` - the running balance WITHIN this
 *     entry's own `ledgerKind`, as if the feed were filtered to that kind
 *     alone. Drives the Business and Trust tables.
 *   - `combinedRunningDebitPositiveMinorUnits` - the running balance ACROSS
 *     the whole interleaved feed, regardless of `ledgerKind`. Drives the
 *     Combined table.
 * Both running balances start from the matching opening balance
 * (`combined.openingDebitPositiveMinorUnits` for the combined column; the
 * entry's own slice opening for the per-ledger column) and accumulate
 * `amountMinorUnits` in feed order. On a single-kind request the two columns
 * are numerically identical.
 */
/**
 * The document-free client account payment a statement row was produced by,
 * on the rows that have one.
 *
 * Null on every other row, and - deliberately - on a row whose payment has
 * been VOIDED. Reconciled payments keep the block for provenance but carry
 * `canVoid: false`, because the remedy is to unmatch the bank line first and
 * most statement users do not have the accounting-gated unmatch workflow.
 *
 * `reference` is the FIRM's own note on the entry, distinct from the row's
 * `description`, which is the client-facing journal label.
 *
 * Current backend feed responses emit `accountPayment` on every row as either
 * this block or null. The optional field shape keeps older local fixtures and
 * clients that predate account-payment metadata parseable during rollout.
 */
export const ClientStatementFeedAccountPaymentSchema = z.object({
    id: z.uuid(),
    fundingSource: PaymentFundingSourceSchema.extract(['BANK_ACCOUNT', 'LEDGER_ACCOUNT']),
    reference: z.string().nullable(),
    canVoid: z.boolean(),
});
export const ClientStatementFeedEntrySchema = CombinedClientStatementEntrySchema.extend({
    accountPayment: ClientStatementFeedAccountPaymentSchema.nullable().optional(),
    amountMinorUnits: z.number().int(),
    runningDebitPositiveMinorUnits: z.number().int(),
    combinedRunningDebitPositiveMinorUnits: z.number().int(),
});
/**
 * One requested ledger's HEADER in a client's statement feed. Discriminated
 * on `status` so a caller cannot read a balance off a ledger the account
 * never provisioned:
 *   - `NOT_PROVISIONED` - the account has no active ledger of this kind. No
 *     zero-balance lie; the caller must narrow on `status` before reading
 *     opening/closing.
 *   - `ACTIVE` - the ledger exists (even when quiet - zero entries in the
 *     period is a legitimate, distinct-from-absent state).
 *
 * Unlike the deleted pre-redesign shape, a slice carries no `entries` of its
 * own: every entry lives once, in the feed's single `entries` array, tagged
 * with `ledgerKind` - filtering that array to one kind reconstructs exactly
 * this slice's per-ledger view (see `ClientStatementFeedEntrySchema`'s
 * `runningDebitPositiveMinorUnits`).
 *
 * Balance fields carry the debit-positive convention in the name
 * (`openingDebitPositiveMinorUnits`, never a bare `openingBalanceMinorUnits`),
 * matching the convention used by the single-ledger statement response.
 */
export const ClientStatementLedgerSliceSchema = z.discriminatedUnion('status', [
    z.object({
        ledgerKind: LedgerKindSchema,
        status: z.literal('NOT_PROVISIONED'),
    }),
    z.object({
        ledgerKind: LedgerKindSchema,
        status: z.literal('ACTIVE'),
        openingDebitPositiveMinorUnits: z.number().int(),
        closingDebitPositiveMinorUnits: z.number().int(),
    }),
]);
/**
 * The client statement page's single read: one interleaved `entries` feed
 * (chronological, `accountingDate` asc, TRUST before BUSINESS on equal
 * dates, `(createdAt, id)` within a kind) plus one header (`slices`) per
 * requested ledger kind, a feed-wide `combined` header (the sum of every
 * ACTIVE slice's opening/closing), and a nullable `position` block.
 *
 * `position` is non-null exactly when BOTH BUSINESS and TRUST were
 * requested and are ACTIVE - the "fees outstanding vs funds held" strip only
 * makes sense with both sides present. The funds-held negation (TRUST's
 * debit-positive ledger sum -> credit-positive "funds held") happens only
 * here, at this named wire boundary. The shared `readClientStatementFeeds`
 * reader stays sign-free. `fundsHeldComposition` is null iff the client has
 * no ClientTrustInvestment rows; when present, pooled is the residual so
 * `pooledMinorUnits + investedMinorUnits === fundsHeldMinorUnits`.
 *
 * The response shape is otherwise INVARIANT under `ledgers` - no field
 * appears or vanishes by parameter value. `ledgers: ['BUSINESS']` issues no
 * TRUST-side query at all; see `readClientStatementFeeds`'s docblock
 * (client-statement domain) for the 404 principle this response's
 * absent-ledger handling follows.
 */
export const ClientStatementFeedsResponseSchema = z.object({
    period: z.object({ from: z.string().nullable(), to: z.string() }),
    currency: z.string(),
    slices: z.array(ClientStatementLedgerSliceSchema),
    entries: z.array(ClientStatementFeedEntrySchema),
    combined: z.object({
        openingDebitPositiveMinorUnits: z.number().int(),
        closingDebitPositiveMinorUnits: z.number().int(),
    }),
    position: z
        .object({
        feesOutstandingMinorUnits: z.number().int(),
        fundsHeldMinorUnits: z.number().int(),
        netPayableByClientMinorUnits: z.number().int(),
        fundsHeldComposition: z
            .object({
            /** Residual: fundsHeldMinorUnits − investedMinorUnits. Signed, funds-held-oriented. */
            pooledMinorUnits: z.number().int(),
            /** s 86(4) claim net of unswept LPFF 5%. Signed, funds-held-oriented. */
            investedMinorUnits: z.number().int(),
        })
            .nullable(), // null iff the client has no ClientTrustInvestment rows
    })
        .nullable(),
});
// Local helper for YYYY-MM-DD validation (prefix `cs` avoids collision with route-file isoDateSchema).
const csIsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const ClientStatementKindSchema = z.enum(['BUSINESS', 'TRUST', 'COMBINED', 'UNPAID_INVOICES']);
export const ClientStatementSchema = z.object({
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    clientId: z.string().uuid(),
    statementKind: ClientStatementKindSchema,
    periodFrom: z.string().nullable(),
    periodTo: z.string(),
    openingBalanceMinorUnits: z.number().int(),
    closingBalanceMinorUnits: z.number().int(),
    currency: z.string(),
    entryCount: z.number().int(),
    pdfStoragePath: z.string(),
    createdByUserId: z.string().uuid().nullable(),
    createdAt: z.string(),
    emailSentAt: z.string().nullable(),
    emailSentToEmail: z.string().nullable(),
    emailSentByUserId: z.string().uuid().nullable(),
    // The invoices this statement covers, captured at snapshot time for a
    // BUSINESS or COMBINED statement - both render the AR entries that raise
    // invoices - and for an UNPAID_INVOICES statement, the invoices it lists. Empty for a TRUST statement (trust movements are not tied to
    // invoice recognition) and for a statement saved before this feature
    // shipped; the latter reads identically to a captured statement whose
    // period genuinely covered no invoices, and the two do not need telling
    // apart: either way there is nothing to attach. The server (`serializeStatement` in
    // `routes/client-statement.ts`) always emits the real array, never a bare
    // missing key - `.optional()` here (not `.default()`) exists purely so an
    // old client bundle - still live against a new Cloud Run revision during a
    // prod deploy window - safe-parses a response from a server revision that
    // predates this field entirely, rather than throwing in the shared API
    // client's response-schema `safeParse` and orphaning an already-persisted
    // statement snapshot. `.default()` would additionally do this, but its
    // input/output type split is what the `api()` client helper's
    // `ZodType<T, T>` response-schema constraint forbids - see the discussion
    // on the same trade-off in `InvoiceCorrectionChangeSchema`
    // (`apps/backend/platform/src/domains/invoice/README.md`, "Particulars corrections: reasons and history").
    // Callers read it with `?? []`.
    coveredInvoiceIds: z.array(z.string().uuid()).optional(),
});
// The list projection of `ClientStatementSchema`, for `GET .../statements`.
// That route is unpaginated (a firm's statement history for one client is
// small) and MCP-exposed, so every row's full `coveredInvoiceIds` array would
// land in the tool result even though nothing needs it until the agent has
// picked one statement - a client with a long statement history can carry
// enough invoice UUIDs to put tens of kilobytes of ids nothing is reading
// into an agent's context in one call. `coveredInvoiceCount` gives the same
// "does this statement cover anything?" signal at a fixed, tiny cost per row;
// callers that need the actual ids call `getClientStatementById` (or reuse
// the array `createClientStatement` just returned).
export const ClientStatementListItemSchema = ClientStatementSchema.omit({
    coveredInvoiceIds: true,
}).extend({
    coveredInvoiceCount: z.number().int().nonnegative(),
});
export const CreateClientStatementInputSchema = z.object({
    statementKind: ClientStatementKindSchema.optional(),
    // Required for BUSINESS, TRUST and COMBINED (the service refuses without
    // it); an UNPAID_INVOICES statement is as at `to` only and ignores it.
    from: csIsoDate.optional(),
    to: csIsoDate.optional(),
    idempotencyKey: z.string().min(1),
});
// =============================================================================
// Unpaid Invoices Statement
// GET /accounts/:accountId/clients/:clientId/unpaid-invoices-statement
// =============================================================================
/** One open business invoice on an unpaid invoices statement, as at its date. */
export const UnpaidInvoiceRowSchema = z.object({
    invoiceId: z.string().uuid(),
    invoiceNumber: z.string().nullable(),
    invoiceDate: csIsoDate,
    /** The invoice's matter reference; null for an invoice with no matter. */
    clientProjectReference: z.string().nullable(),
    /** The effective due date the debtors age analysis ages from. */
    dueDate: csIsoDate,
    totalMinorUnits: z.number().int(),
    /** Total less outstanding. */
    paidOrCreditedMinorUnits: z.number().int(),
    outstandingMinorUnits: z.number().int(),
    /** Whole days past the due date as at the statement date; zero or less reads "Not yet due". */
    daysOverdue: z.number().int(),
});
/**
 * A client's open business invoices as at one date, with one signed line for
 * the rest of the client's business balance, so `totalDueMinorUnits` equals
 * the business statement's closing balance for the same date by construction:
 * `unpaidInvoicesMinorUnits + unlinkedBalanceMinorUnits === totalDueMinorUnits`.
 * With `clientProjectId` set it lists one matter's open invoices instead, and
 * the total is theirs alone (see `totalDueMinorUnits`).
 */
export const UnpaidInvoicesStatementResponseSchema = z.object({
    asOf: csIsoDate,
    currency: z.string(),
    /** Ordered by invoice date, then due date, then invoice number. */
    rows: z.array(UnpaidInvoiceRowSchema),
    /** The sum of the rows' outstanding amounts. */
    unpaidInvoicesMinorUnits: z.number().int(),
    /**
     * The business balance no row carries: unallocated payments and credits,
     * over-credited invoices, and invoices the ledger cannot link to a row.
     * Negative reads "Less: unallocated credits", positive "Add: balance not
     * linked to an invoice", and zero is omitted (`describeUnlinkedBalance`).
     */
    unlinkedBalanceMinorUnits: z.number().int(),
    /**
     * The client's all-time business closing balance as at `asOf`;
     * debit-positive. Narrowed to a matter, the sum of the rows instead, with
     * `unlinkedBalanceMinorUnits` zero: unallocated credits are not attributed
     * to a matter, so a matter total can exceed what the client owes overall.
     */
    totalDueMinorUnits: z.number().int(),
    /**
     * The matter the statement is narrowed to: only invoices whose own matter
     * it is are rows. Null or absent for a client-level statement. Optional so
     * an old client bundle safe-parses a response from a newer server.
     */
    clientProjectId: z.string().uuid().nullable().optional(),
});
// Bounds the send-path invoice-attachment render loop (see `renderInvoiceEmailAttachments`,
// invoice domain) - the schema cap is that loop's documented bound.
//
// 20, chosen for the RECIPIENT's mailbox rather than for our own limits.
//
// Size the cap the way a mail server does, in ENCODED bytes: a MIME attachment
// travels base64-encoded, which inflates it by ~4/3 (`encodedAttachmentBytes`,
// invoice domain). At a realistic 150-300KB per invoice PDF, 20 invoices are
// 3-6MB raw but 4-8MB on the wire, plus the statement PDF itself and MIME
// overhead. Against a corporate mail system capping at 10MB that leaves under
// 2MB of headroom at the top of the range - it fits, but the margin is thin,
// and an unusually heavy set of invoices could exceed it. Against Google's and
// Microsoft's 25MB it is comfortable with room to spare.
//
// Our own ceiling (MAX_ENCODED_EMAIL_ATTACHMENT_BYTES, 28MB encoded) sits under
// SendGrid's send limit but well above what many recipients accept, so a count
// that maxed it out would spend seconds of render time producing an email that
// bounces.
export const MAX_STATEMENT_INVOICE_ATTACHMENTS = 20;
export const SendClientStatementInputSchema = z.object({
    to: z.array(z.string().email()).min(1).max(10),
    cc: z.array(z.string().email()).max(10).optional(),
    bcc: z.array(z.string().email()).max(MAX_SEND_BCC_RECIPIENTS).optional(),
    // When true, the server appends the sending user's own email to bcc
    // (de-duplicated against to/cc/bcc). Mirrors the invoice send path so the
    // sender never has to be a client email or account member to receive a copy.
    bccSender: z.boolean().optional(),
    idempotencyKey: z.string().min(1),
    // Invoices to attach to the email alongside the statement PDF, intersected
    // against the statement's own captured `coveredInvoiceIds` server-side - an
    // id outside that set (or any id, when the set was never captured) is
    // silently dropped rather than erroring. Omitted or empty means today's
    // behaviour: the statement PDF only.
    //
    // Normalised to lower case here, at the request boundary, rather than in the
    // domain service, so the intersection against `coveredInvoiceIds`, the
    // invoice lookup and the idempotency payload all see one casing (see
    // `LowercaseUuidSchema`).
    attachInvoiceIds: z
        .array(LowercaseUuidSchema)
        .max(MAX_STATEMENT_INVOICE_ATTACHMENTS)
        .optional(),
});
export const ListClientStatementsResponseSchema = z.object({
    statements: z.array(ClientStatementListItemSchema),
});
export const SendClientStatementResponseSchema = z.object({
    messageId: z.string(),
});
// =============================================================================
// Client Trust Balance
// GET /accounts/:accountId/clients/:clientId/trust-balance
// =============================================================================
/**
 * A client's live trust balance in the account's TRUST ledger. Uses the
 * ledger's debit-positive convention for the TRUST_CREDITORS control account:
 * negative means funds held for the client, positive means a trust shortfall.
 */
export const ClientTrustBalanceResponseSchema = z.object({
    clientId: z.string().uuid(),
    balanceMinorUnits: z.number().int(),
});
// =============================================================================
// Trust Creditors Report
// GET /accounts/:accountId/ledgers/:ledgerId/trust-creditors
// =============================================================================
/**
 * One current-asset account's balance in the trust creditors report's assets
 * section: every active CURRENT_ASSETS account on the ledger, not a
 * hardcoded role allow-list - the s86(2) trust bank account
 * (TRUST_BANK_S86_2), the uncleared Trust Payments Clearing account (the
 * kind-agnostic PAYMENTS_CLEARING role - money recorded in a client's trust
 * ledger but not yet confirmed on the bank statement), and any custom trust
 * asset account a firm has added (`systemRole: null`). `systemRole` lets the
 * frontend badge a clearing row as "System" (same convention as
 * TrialBalanceTable's isClearingRole); null for a custom account.
 */
export const TrustCreditorsReportAssetSchema = z.object({
    ledgerAccountId: z.string(),
    code: z.string(),
    name: z.string(),
    balanceMinorUnits: z.number().int(),
    systemRole: LedgerAccountSystemRoleSchema.nullable(),
});
/**
 * One client's funds-held balance in the trust creditors report's creditors
 * section. Natural-liability-positive sign convention (same as
 * `TrustCreditorsReportOtherLiability` and the Balance Sheet's LIABILITY
 * section): positive is a normal credit balance (funds held for the
 * client); a negative balance is a trust shortfall (the practice paid out
 * more than it held) - the frontend flags this with a highlighted row and a
 * "Debit" badge, since it is a serious compliance issue.
 */
export const TrustCreditorsReportCreditorSchema = z.object({
    clientId: z.string().uuid(),
    clientName: z.string(),
    balanceMinorUnits: z.number().int(),
});
/**
 * One other-liability account's balance in the trust creditors report: every
 * active CURRENT_LIABILITIES account other than the TRUST_CREDITORS control
 * account (which is broken down per client in `creditors` instead) - not a
 * hardcoded role allow-list. On a preset-seeded trust ledger that is
 * TRUST_INTEREST_PAYABLE_LPFF (interest owed to the Legal Practitioners'
 * Fidelity Fund, not to any client) and TRUST_UNKNOWN_OWNER (trust money held
 * whose owner is not yet identified, so it cannot sit under a named client);
 * because the section is filtered rather than allow-listed, any custom trust
 * liability a firm adds surfaces here too, with no code change.
 */
export const TrustCreditorsReportOtherLiabilitySchema = z.object({
    ledgerAccountId: z.string(),
    code: z.string(),
    name: z.string(),
    balanceMinorUnits: z.number().int(),
});
/**
 * Three-section trust creditors report: trust assets (every active
 * CURRENT_ASSETS account), trust creditors (funds held per client), and
 * other trust liabilities (every active CURRENT_LIABILITIES account other
 * than TRUST_CREDITORS) - the Rule 54.15.1 reconciliation view.
 * `assetsTotalMinorUnits` equals `creditorsTotalMinorUnits +
 * otherLiabilitiesTotalMinorUnits` on a reconciled trust ledger, since a
 * TRUST ledger has no EQUITY/INCOME/EXPENSE sections in practice.
 */
export const TrustCreditorsReportResponseSchema = z.object({
    currency: z.string(),
    // ISO date-time, end of the as-at day UTC - the same resolved cutoff
    // `TrialBalance.asOf` carries (both sections are derived from one
    // resolution; see `getTrustCreditorsReport`). Null only on a ledger with no
    // financial year end and no explicit `at` supplied (all-time balance).
    asOf: z.string().nullable(),
    assets: z.array(TrustCreditorsReportAssetSchema),
    assetsTotalMinorUnits: z.number().int(),
    creditors: z.array(TrustCreditorsReportCreditorSchema),
    creditorsTotalMinorUnits: z.number().int(),
    otherLiabilities: z.array(TrustCreditorsReportOtherLiabilitySchema),
    otherLiabilitiesTotalMinorUnits: z.number().int(),
});
/**
 * The trust reconciliation at several dates at once, oldest first, the anchor
 * date last. Every column is a full `TrustCreditorsReportResponse` as at its
 * own date - the series adds no new figures, only more dates.
 *
 * The reconciliation invariant is PER COLUMN, never across the series: on each
 * date `assetsTotalMinorUnits` must equal `creditorsTotalMinorUnits +
 * otherLiabilitiesTotalMinorUnits`. That is exactly what makes the multi-date
 * view worth having for Rule 54.15.1 - a practice can see at a glance which
 * month the trust position stopped reconciling, rather than discovering it only
 * at the date it happens to be looking at.
 *
 * A client's row appears in a column only when it held a non-zero balance on
 * that date (`getAllClientTrustBalances` drops zero-net clients). The
 * presentation layer unions the client set across columns and reads an absent
 * client as a genuine 0.00 - see `seriesAmountsForKey`.
 */
export const TrustCreditorsReportSeriesColumnSchema = TrustCreditorsReportResponseSchema.extend({
    /**
     * Non-null here, unlike on the single-date response - a series always has a
     * resolved anchor, so a column with no date to label it cannot arise. Same
     * tightening, same reason, as `TrialBalanceSeriesColumnSchema`.
     */
    asOf: z.string(),
});
export const TrustCreditorsReportSeriesSchema = z.object({
    columns: z.array(TrustCreditorsReportSeriesColumnSchema).min(MIN_AS_OF_SERIES_COLUMNS).max(MAX_AS_OF_SERIES_COLUMNS),
});
